import React, { useState, useEffect } from 'react';
import { getMenus, createMenu, updateMenu, deleteMenu, toggleMenuStatus } from '../services/api';
import toast from 'react-hot-toast';

const AdminMenuManager = () => {
  const [allMenus, setAllMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeMenuType, setActiveMenuType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    setLoading(true);
    try {
      const data = await getMenus();
      let menusArray = [];
      if (Array.isArray(data)) {
        menusArray = data;
      } else if (data.data && Array.isArray(data.data)) {
        menusArray = data.data;
      }
      setAllMenus(menusArray);
    } catch (error) {
      console.error('Error fetching menus:', error);
      toast.error('Error fetching menus from database');
      setAllMenus([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (menuData) => {
    try {
      // Prepare data for API - ensure parentId is properly handled
      const submitData = {
        ...menuData,
        parentId: menuData.parentId === '' || menuData.parentId === 'null' || menuData.parentId === null ? null : menuData.parentId
      };
      
      if (editingItem) {
        await updateMenu(editingItem._id, submitData);
        toast.success('Menu updated successfully');
      } else {
        await createMenu(submitData);
        toast.success('Menu created successfully');
      }
      await fetchMenus();
      setShowModal(false);
      setEditingItem(null);
    } catch (error) {
      const errorMessage = error.message || 'Error saving menu';
      if (errorMessage.includes('already exists')) {
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleDelete = async (id, menuName) => {
    if (window.confirm(`Are you sure you want to delete "${menuName}"? This action cannot be undone.`)) {
      try {
        await deleteMenu(id);
        toast.success('Menu deleted successfully');
        await fetchMenus();
      } catch (error) {
        toast.error('Error deleting menu');
      }
    }
  };

  const handleToggleStatus = async (id, currentStatus, menuName) => {
    try {
      await toggleMenuStatus(id);
      toast.success(`"${menuName}" ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      await fetchMenus();
    } catch (error) {
      toast.error('Error toggling status');
    }
  };

  const handleUpdateOrder = async (id, newOrder) => {
    try {
      const menu = allMenus.find(m => m._id === id);
      await updateMenu(id, { ...menu, order: parseInt(newOrder) });
      await fetchMenus();
      toast.success('Order updated');
    } catch (error) {
      toast.error('Error updating order');
    }
  };

  // Group menus by type
  const headerMenus = allMenus.filter(menu => menu.type === 'header').sort((a, b) => a.order - b.order);
  const footerMenus = allMenus.filter(menu => menu.type === 'footer').sort((a, b) => a.order - b.order);
  const sidebarMenus = allMenus.filter(menu => menu.type === 'sidebar' || menu.type === 'admin').sort((a, b) => a.order - b.order);

  const menuTypes = [
    { id: 'all', name: 'All Menus', icon: 'fas fa-bars', color: '#2C7A7B', count: allMenus.length },
    { id: 'header', name: 'Header Menu', icon: 'fas fa-arrow-up', color: '#3498db', count: headerMenus.length },
    { id: 'footer', name: 'Footer Menu', icon: 'fas fa-arrow-down', color: '#F5A623', count: footerMenus.length },
    { id: 'sidebar', name: 'Sidebar Menu', icon: 'fas fa-columns', color: '#9b59b6', count: sidebarMenus.length }
  ];

  const getDisplayMenus = () => {
    if (activeMenuType === 'all') {
      return [
        { type: 'header', title: 'Header Menu', icon: 'fas fa-arrow-up', color: '#3498db', menus: headerMenus },
        { type: 'footer', title: 'Footer Menu', icon: 'fas fa-arrow-down', color: '#F5A623', menus: footerMenus },
        { type: 'sidebar', title: 'Sidebar Menu', icon: 'fas fa-columns', color: '#9b59b6', menus: sidebarMenus }
      ];
    } else if (activeMenuType === 'header') {
      return [{ type: 'header', title: 'Header Menu', icon: 'fas fa-arrow-up', color: '#3498db', menus: headerMenus }];
    } else if (activeMenuType === 'footer') {
      return [{ type: 'footer', title: 'Footer Menu', icon: 'fas fa-arrow-down', color: '#F5A623', menus: footerMenus }];
    } else if (activeMenuType === 'sidebar') {
      return [{ type: 'sidebar', title: 'Sidebar Menu', icon: 'fas fa-columns', color: '#9b59b6', menus: sidebarMenus }];
    }
    return [];
  };

  const filterMenusBySearch = (menus) => {
    if (!searchTerm) return menus;
    return menus.filter(menu => 
      menu.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      menu.path?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const commonIcons = ['🏠', 'ℹ️', '📦', '⚙️', '🎓', '🤝', '👥', '💬', '📅', '⭐', '💼', '📚', '🔧', '🎯', '💡', '🔒', '🚀', '🌟', '❤️', '💎'];
  const fontAwesomeIcons = [
    'fas fa-home', 'fas fa-info-circle', 'fas fa-box', 'fas fa-cogs', 'fas fa-graduation-cap', 
    'fas fa-handshake', 'fas fa-users', 'fas fa-comment', 'fas fa-calendar', 'fas fa-star',
    'fas fa-briefcase', 'fas fa-book', 'fas fa-wrench', 'fas fa-bullseye', 'fas fa-lightbulb',
    'fas fa-lock', 'fas fa-rocket', 'fas fa-gem', 'fas fa-heart', 'fas fa-chart-line'
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="spinner" style={{
          width: '40px',
          height: '40px',
          border: '4px solid #E2E8F0',
          borderTopColor: '#2C7A7B',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 1rem'
        }} />
        <p>Loading menus...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const displayGroups = getDisplayMenus();

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#0A192F', marginBottom: '0.5rem' }}>Menu Manager</h2>
        <p style={{ color: '#5A6E7F' }}>Manage all menus across your website including header, footer, and sidebar</p>
      </div>

      {/* Menu Type Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        borderBottom: '2px solid #E2E8F0',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        paddingBottom: '0'
      }}>
        {menuTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => setActiveMenuType(type.id)}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeMenuType === type.id ? type.color : 'transparent',
              color: activeMenuType === type.id ? 'white' : '#5A6E7F',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s'
            }}
          >
            <i className={type.icon} />
            {type.name}
            <span style={{
              background: activeMenuType === type.id ? 'rgba(255,255,255,0.2)' : '#E2E8F0',
              padding: '0.2rem 0.5rem',
              borderRadius: '20px',
              fontSize: '0.7rem',
              marginLeft: '0.5rem'
            }}>
              {type.count}
            </span>
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
          <i className="fas fa-search" style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#94A3B8'
          }} />
          <input
            type="text"
            placeholder="Search menus by name or path..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem 0.5rem 0.5rem 2rem',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              fontSize: '0.875rem'
            }}
          />
        </div>
        <button
          onClick={() => {
            setEditingItem(null);
            setShowModal(true);
          }}
          style={{
            padding: '0.5rem 1rem',
            background: '#2C7A7B',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <i className="fas fa-plus" />
          Add Menu Item
        </button>
      </div>

      {/* Menu Groups */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        {displayGroups.map((group) => {
          const filteredMenus = filterMenusBySearch(group.menus);
          
          return (
            <div key={group.type} style={{
              background: 'white',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              {/* Group Header */}
              <div style={{
                background: `linear-gradient(135deg, ${group.color} 0%, ${group.color}CC 100%)`,
                padding: '1rem 1.5rem',
                color: 'white'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <i className={group.icon} style={{ fontSize: '1.2rem' }} />
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
                    {group.title} ({filteredMenus.length} {filteredMenus.length === 1 ? 'item' : 'items'})
                  </h3>
                </div>
              </div>

              {/* Menu Items Table */}
              {filteredMenus.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#F7F9FC', borderBottom: '1px solid #E2E8F0' }}>
                      <tr>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', width: '60px' }}>Order</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', width: '60px' }}>Icon</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Name</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Path / URL</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', width: '100px' }}>Parent</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', width: '80px' }}>Status</th>
                        <th style={{ padding: '0.75rem 1rem', textAlign: 'left', width: '180px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMenus.map((menu) => {
                        const parentMenu = allMenus.find(m => m._id === menu.parentId);
                        return (
                          <tr key={menu._id} style={{ borderBottom: '1px solid #E2E8F0' }}>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <input
                                type="number"
                                value={menu.order}
                                onChange={(e) => handleUpdateOrder(menu._id, e.target.value)}
                                style={{ width: '60px', padding: '0.25rem', border: '1px solid #E2E8F0', borderRadius: '4px' }}
                              />
                            </td>
                            <td style={{ padding: '0.75rem 1rem', fontSize: '1.2rem', textAlign: 'center' }}>
                              {menu.icon || '📄'}
                            </td>
                            <td style={{ padding: '0.75rem 1rem', fontWeight: 500 }}>
                              {menu.name}
                              {menu.parentId && (
                                <span style={{ 
                                  marginLeft: '0.5rem', 
                                  fontSize: '0.7rem', 
                                  color: '#94A3B8',
                                  background: '#F1F5F9',
                                  padding: '0.2rem 0.4rem',
                                  borderRadius: '4px'
                                }}>
                                  <i className="fas fa-level-down-alt" style={{ marginRight: '0.2rem' }} />
                                  submenu
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <code style={{ background: '#F7F9FC', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.8rem' }}>
                                {menu.path}
                              </code>
                            </td>
                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: '#5A6E7F' }}>
                              {parentMenu ? parentMenu.name : '—'}
                            </td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <button
                                onClick={() => handleToggleStatus(menu._id, menu.isActive, menu.name)}
                                style={{
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '20px',
                                  border: 'none',
                                  background: menu.isActive ? '#D4EDDA' : '#F8D7DA',
                                  color: menu.isActive ? '#155724' : '#721C24',
                                  cursor: 'pointer',
                                  fontSize: '0.7rem'
                                }}
                              >
                                {menu.isActive ? 'Active' : 'Inactive'}
                              </button>
                            </td>
                            <td style={{ padding: '0.75rem 1rem' }}>
                              <button
                                onClick={() => {
                                  setEditingItem(menu);
                                  setShowModal(true);
                                }}
                                style={{
                                  padding: '0.4rem 0.8rem',
                                  background: '#2C7A7B',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  marginRight: '0.5rem',
                                  fontSize: '0.8rem',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.4rem'
                                }}
                              >
                                <i className="fas fa-edit" style={{ fontSize: '0.8rem' }} />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(menu._id, menu.name)}
                                style={{
                                  padding: '0.4rem 0.8rem',
                                  background: '#DC2626',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.4rem'
                                }}
                              >
                                <i className="fas fa-trash-alt" style={{ fontSize: '0.8rem' }} />
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#5A6E7F' }}>
                  <i className="fas fa-inbox" style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
                  <p>No {group.title.toLowerCase()} items found</p>
                  <button
                    onClick={() => {
                      setEditingItem(null);
                      setShowModal(true);
                    }}
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.25rem 0.75rem',
                      background: '#2C7A7B',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    + Add {group.title} Item
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* No Results */}
      {displayGroups.every(group => filterMenusBySearch(group.menus).length === 0) && searchTerm && (
        <div style={{ textAlign: 'center', padding: '3rem', background: 'white', borderRadius: '16px', marginTop: '1rem' }}>
          <i className="fas fa-search" style={{ fontSize: '3rem', color: '#CBD5E1', marginBottom: '1rem' }} />
          <p style={{ color: '#5A6E7F' }}>No menus found matching "{searchTerm}"</p>
        </div>
      )}

      {/* Modal for Add/Edit */}
      {showModal && (
        <MenuModal
          initialData={editingItem}
          allMenus={allMenus}
          menuTypes={menuTypes}
          commonIcons={commonIcons}
          fontAwesomeIcons={fontAwesomeIcons}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowModal(false);
            setEditingItem(null);
          }}
        />
      )}
    </div>
  );
};

// Menu Form Component with Parent Item Support
const MenuModal = ({ initialData, allMenus, menuTypes, commonIcons, fontAwesomeIcons, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    path: initialData?.path || '',
    type: initialData?.type || 'header',
    parentId: initialData?.parentId || '',
    order: initialData?.order || 0,
    icon: initialData?.icon || '📄',
    iconType: initialData?.icon?.startsWith('fas') ? 'fontawesome' : 'emoji',
    target: initialData?.target || '_self',
    isActive: initialData?.isActive !== undefined ? initialData.isActive : true
  });

  const [useFontAwesome, setUseFontAwesome] = useState(formData.iconType === 'fontawesome');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Ensure parentId is properly set (empty string becomes null)
    const submitData = {
      ...formData,
      parentId: formData.parentId === '' ? null : formData.parentId
    };
    onSubmit(submitData);
  };

  // Get parent menu options (exclude current menu when editing)
  const parentOptions = allMenus.filter(menu => 
    menu.type === formData.type && 
    (!initialData || menu._id !== initialData._id) &&
    (menu.parentId === null || menu.parentId === undefined) // Only top-level menus can be parents
  );

  const typeOptions = [
    { value: 'header', label: 'Header Menu', icon: 'fas fa-arrow-up', color: '#3498db', description: 'Main navigation menu at the top of the website' },
    { value: 'footer', label: 'Footer Menu', icon: 'fas fa-arrow-down', color: '#F5A623', description: 'Links displayed in the footer section' },
    { value: 'sidebar', label: 'Sidebar Menu', icon: 'fas fa-columns', color: '#9b59b6', description: 'Sidebar navigation menu (admin panel)' }
  ];

  const selectedType = typeOptions.find(t => t.value === formData.type);

  // Helper to get path suggestion
  const getPathSuggestion = () => {
    if (!formData.name) return '';
    const slug = formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (formData.type === 'header') return `/${slug}`;
    if (formData.type === 'footer') return `/${slug}`;
    if (formData.type === 'sidebar') return `/admin/${slug}`;
    return `/${slug}`;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onCancel}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '2rem',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '85vh',
        overflow: 'auto'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ color: '#0A192F', margin: 0 }}>
            <i className={`fas ${initialData ? 'fa-edit' : 'fa-plus'}`} style={{ marginRight: '0.5rem', color: '#2C7A7B' }} />
            {initialData ? 'Edit Menu Item' : 'Add New Menu Item'}
          </h3>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* Menu Type */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0A192F' }}>Menu Type *</label>
            <select
              required
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', background: 'white' }}
            >
              {typeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {selectedType && (
              <small style={{ color: selectedType.color, marginTop: '0.25rem', display: 'block' }}>
                <i className={selectedType.icon} /> {selectedType.description}
              </small>
            )}
          </div>

          {/* Menu Name */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0A192F' }}>
              Menu Name <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Home, About Us, Products"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
            />
            <small style={{ color: '#5A6E7F' }}>Display name for this menu item</small>
          </div>
          
          {/* Path / URL */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0A192F' }}>
              Path / URL <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <input
              type="text"
              required
              value={formData.path}
              onChange={(e) => setFormData({ ...formData, path: e.target.value })}
              placeholder="e.g., /about, https://example.com"
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
            />
            <small style={{ color: '#5A6E7F' }}>
              <i className="fas fa-info-circle" /> Internal links start with <code>/</code>, external links with <code>http://</code> or <code>https://</code>
              {formData.name && !formData.path && (
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, path: getPathSuggestion() })}
                  style={{
                    marginLeft: '0.5rem',
                    padding: '0.2rem 0.5rem',
                    background: '#E6F7F5',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.7rem',
                    color: '#2C7A7B'
                  }}
                >
                  Suggest: {getPathSuggestion()}
                </button>
              )}
            </small>
          </div>

          {/* Parent Item */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0A192F' }}>
              Parent Item
            </label>
            <select
              value={formData.parentId || ''}
              onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px', background: 'white' }}
            >
              <option value="">None (Top Level)</option>
              {parentOptions.map(menu => (
                <option key={menu._id} value={menu._id}>
                  {menu.icon} {menu.name}
                </option>
              ))}
            </select>
            <small style={{ color: '#5A6E7F' }}>
              <i className="fas fa-level-down-alt" /> Select a parent menu to create a submenu item
            </small>
          </div>

          {/* Icon Selection */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0A192F' }}>Icon</label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setUseFontAwesome(false)}
                style={{
                  padding: '0.3rem 0.8rem',
                  background: !useFontAwesome ? '#2C7A7B' : '#F7F9FC',
                  color: !useFontAwesome ? 'white' : '#5A6E7F',
                  border: '1px solid #E2E8F0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                😊 Emoji
              </button>
              <button
                type="button"
                onClick={() => setUseFontAwesome(true)}
                style={{
                  padding: '0.3rem 0.8rem',
                  background: useFontAwesome ? '#2C7A7B' : '#F7F9FC',
                  color: useFontAwesome ? 'white' : '#5A6E7F',
                  border: '1px solid #E2E8F0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                <i className="fab fa-font-awesome" /> Font Awesome
              </button>
            </div>

            {!useFontAwesome ? (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                  {commonIcons.slice(0, 15).map(icon => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      style={{
                        fontSize: '1.2rem',
                        padding: '0.5rem',
                        background: formData.icon === icon ? '#2C7A7B' : '#F7F9FC',
                        border: `1px solid ${formData.icon === icon ? '#2C7A7B' : '#E2E8F0'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        width: '42px',
                        height: '42px'
                      }}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="Enter custom emoji (e.g., 🏠, 📦, ⚙️)"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                />
              </>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                  {fontAwesomeIcons.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      style={{
                        fontSize: '1rem',
                        padding: '0.5rem',
                        background: formData.icon === icon ? '#2C7A7B' : '#F7F9FC',
                        color: formData.icon === icon ? 'white' : '#2C7A7B',
                        border: `1px solid ${formData.icon === icon ? '#2C7A7B' : '#E2E8F0'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        width: '42px',
                        height: '42px'
                      }}
                    >
                      <i className={icon} />
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="Enter FontAwesome class (e.g., fas fa-home)"
                  style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                />
              </>
            )}
            <small style={{ color: '#5A6E7F' }}>Choose an icon to display next to the menu item</small>
          </div>
          
          {/* Display Order */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0A192F' }}>Display Order</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #E2E8F0', borderRadius: '8px' }}
            />
            <small style={{ color: '#5A6E7F' }}>Lower numbers appear first in the menu</small>
          </div>
          
          {/* Open in new tab */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#0A192F' }}>Open Link In</label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="_self"
                  checked={formData.target === '_self'}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                />
                Same Window
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  value="_blank"
                  checked={formData.target === '_blank'}
                  onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                />
                New Window/Tab
              </label>
            </div>
          </div>
          
          {/* Active Status */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span>Active (visible on website)</span>
            </label>
          </div>
          
          {/* Form Actions */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #E2E8F0' }}>
            <button type="submit" style={{ flex: 1, padding: '0.75rem', background: '#2C7A7B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              <i className="fas fa-save" /> {initialData ? 'Update Menu' : 'Create Menu'}
            </button>
            <button type="button" onClick={onCancel} style={{ flex: 1, padding: '0.75rem', background: '#5A6E7F', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminMenuManager;